/**
 * Procedural wood material for parametric beams.
 *
 * The pattern is born from a repeatable CanvasTexture: it does not require external assets
 * and continues to work after shear or CSG cuts, because it uses the UV of the
 * resulting geometry.*/

import * as THREE from "three";
import { COLORS } from "../../config/colors";
import { WOOD_TEXTURE_DEFAULTS } from "../../config/defaults";

export interface WoodMaterialOptions {
  color: string;
  opacity: number;
  lengthMm: number;
  sectionMm: number;
}

export interface WoodSurface {
  materials: THREE.MeshStandardMaterial[];
  textures: THREE.CanvasTexture[];
}

export function createWoodSurface(options: WoodMaterialOptions): WoodSurface {
  const sideTexture = createWoodTexture("side", options.lengthMm, options.sectionMm);
  const endTexture = createWoodTexture("end", options.lengthMm, options.sectionMm);
  const materialParameters = {
    color: options.color,
    roughness: 0.8,
    metalness: 0.08,
    transparent: options.opacity < 1,
    opacity: options.opacity,
    side: THREE.DoubleSide,
  };

  return {
    materials: [
      new THREE.MeshStandardMaterial({ ...materialParameters, map: sideTexture }),
      new THREE.MeshStandardMaterial({ ...materialParameters, map: endTexture }),
    ],
    textures: [sideTexture, endTexture],
  };
}

function createWoodTexture(kind: "side" | "end", lengthMm: number, sectionMm: number): THREE.CanvasTexture {
  const {
    canvasSize,
    lamellaCount,
    grainStepPx,
    grainLineWidth,
    lamellaLineWidth,
    endRingStartPx,
    endRingStepPx,
    endRingMaxPx,
    endRingCenterOffsetPx,
    endRingCenterVerticalOffsetPx,
    repeatLengthMm,
    repeatSectionMm,
  } = WOOD_TEXTURE_DEFAULTS;
  const canvas = document.createElement("canvas");
  canvas.width = canvasSize;
  canvas.height = canvasSize;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Unable to create the procedural wood texture canvas.");

  context.fillStyle = COLORS.woodBase;
  context.fillRect(0, 0, canvas.width, canvas.height);
  const lamellaHeight = canvas.height / lamellaCount;

  if (kind === "side") {
    context.strokeStyle = COLORS.woodGrain;
    context.lineWidth = grainLineWidth;
    for (let y = grainStepPx; y < canvas.height; y += grainStepPx) {
      const wave = Math.sin(y * 0.11) * 3;
      context.beginPath();
      context.moveTo(0, y);
      context.bezierCurveTo(canvas.width / 3, y + wave, (canvas.width * 2) / 3, y - wave, canvas.width, y);
      context.stroke();
    }

    drawLamellaLines(context, lamellaHeight, lamellaCount, lamellaLineWidth);
  } else {
    context.strokeStyle = COLORS.woodGrain;
    context.lineWidth = grainLineWidth;
    for (let lamellaIndex = 0; lamellaIndex < lamellaCount; lamellaIndex += 1) {
      const startY = lamellaIndex * lamellaHeight;
      const centerX = canvas.width / 2 + Math.sin(lamellaIndex * 2.17) * endRingCenterOffsetPx;
      const centerY = lamellaIndex % 2 === 0 ? startY - endRingCenterVerticalOffsetPx : startY + lamellaHeight + endRingCenterVerticalOffsetPx;
      context.save();
      context.beginPath();
      context.rect(0, startY, canvas.width, lamellaHeight);
      context.clip();
      for (let radius = endRingStartPx; radius < endRingMaxPx; radius += endRingStepPx) {
        context.beginPath();
        context.arc(centerX, centerY, radius, 0, Math.PI * 2);
        context.stroke();
      }
      context.restore();
    }
    drawLamellaLines(context, lamellaHeight, lamellaCount, lamellaLineWidth);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(
    Math.max(1, lengthMm / repeatLengthMm),
    Math.max(1, sectionMm / repeatSectionMm),
  );
  return texture;
}

function drawLamellaLines(
  context: CanvasRenderingContext2D,
  lamellaHeight: number,
  lamellaCount: number,
  lineWidth: number,
): void {
  context.strokeStyle = COLORS.woodLamella;
  context.globalAlpha = 0.45;
  context.lineWidth = lineWidth;
  for (let index = 1; index < lamellaCount; index += 1) {
    const y = index * lamellaHeight;
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(context.canvas.width, y);
    context.stroke();
  }
  context.globalAlpha = 1;
}

/** Assign material 1 to the local end faces of the beam (X axis).*/
export function assignWoodMaterialGroups(geometry: THREE.BufferGeometry): void {
  const position = geometry.getAttribute("position");
  if (!position) return;

  const index = geometry.getIndex();
  const triangleCount = index ? index.count / 3 : position.count / 3;
  geometry.clearGroups();

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const normal = new THREE.Vector3();
  for (let triangle = 0; triangle < triangleCount; triangle += 1) {
    const offset = triangle * 3;
    const ia = index ? index.getX(offset) : offset;
    const ib = index ? index.getX(offset + 1) : offset + 1;
    const ic = index ? index.getX(offset + 2) : offset + 2;
    a.fromBufferAttribute(position, ia);
    b.fromBufferAttribute(position, ib);
    c.fromBufferAttribute(position, ic);
    normal.crossVectors(b.sub(a), c.sub(a)).normalize();
    const endFace = Math.abs(normal.x) > Math.abs(normal.y) && Math.abs(normal.x) > Math.abs(normal.z);
    geometry.addGroup(offset, 3, endFace ? 1 : 0);
  }
}
